'use client'

// @ts-strict-ignore
import clsx from 'clsx'
import { FC, useRef, useState } from 'react'

import { Input, InputProps } from '@/shared/ui'

function formatPhone(digits: string): string {
	const d = digits.slice(0, 10)
	let result = '+7'
	if (d.length === 0) return result
	result += ` (${d.slice(0, 3)}`
	if (d.length < 3) return result
	result += `) ${d.slice(3, 6)}`
	if (d.length < 6) return result
	result += `-${d.slice(6, 8)}`
	if (d.length < 8) return result
	result += `-${d.slice(8, 10)}`
	return result
}

function extractDigits(value: string): string {
	const raw = value.replace(/\D/g, '')
	if (raw.startsWith('7') || raw.startsWith('8')) return raw.slice(1)
	return raw
}

// Возвращает позицию курсора после N цифр (не считая кода страны)
function cursorAfterDigit(formatted: string, digitIndex: number): number {
	let seen = -1 // -1 т.к. первая цифра — это "7" кода страны
	for (let i = 0; i < formatted.length; i++) {
		if (/\d/.test(formatted[i])) {
			seen++
			if (seen === digitIndex) return i + 1
		}
	}
	return formatted.length
}

/**
 * Input with Russian phone mask (+7 (XXX) XXX-XX-XX)
 * Drop-in replacement for the react-input-mask based version
 */
export const MaskedPhoneInput: FC<Partial<InputProps>> = props => {
	const {
		onChange,
		onMouseDown,
		onFocus,
		onBlur,
		value,
		disabled,
		readOnly,
		label = 'Номер телефона',
		className,
		...inputProps
	} = props
	const inputRef = useRef<HTMLInputElement>(null)
	const [isFocused, setIsFocused] = useState(false)

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const digits = extractDigits(e.target.value)
		const formatted = formatPhone(digits)
		onChange?.(formatted as any)
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		const input = inputRef.current
		if (!input) return

		const cursor = input.selectionStart ?? 0
		const selectionEnd = input.selectionEnd ?? 0
		const hasSelection = cursor !== selectionEnd

		if (e.key === 'Backspace' && !hasSelection) {
			e.preventDefault()
			const current = value?.toString() || ''
			const allDigits = extractDigits(current)

			// Найти последнюю цифру перед курсором
			let digitPos = cursor - 1
			while (digitPos >= 0 && !/\d/.test(current[digitPos])) {
				digitPos--
			}

			if (digitPos < 2) return // не трогаем +7

			// Считаем индекс этой цифры среди цифр строки (включая "7")
			let digitsBefore = 0
			for (let i = 0; i <= digitPos; i++) {
				if (/\d/.test(current[i])) digitsBefore++
			}
			// digitsBefore включает "7" кода страны, индекс в allDigits = digitsBefore - 2
			const idx = digitsBefore - 2
			if (idx < 0) return

			const newDigits = allDigits.slice(0, idx) + allDigits.slice(idx + 1)
			const formatted = formatPhone(newDigits)
			onChange?.(formatted as any)

			requestAnimationFrame(() => {
				if (!inputRef.current) return
				const newPos = cursorAfterDigit(formatted, idx) // курсор перед удалённой позицией
				inputRef.current.setSelectionRange(newPos, newPos)
			})
		}
	}

	const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		setIsFocused(true)

		if (!value || value.toString().length < 2) {
			onChange?.('+7' as any)
		}
		onFocus?.(e)
	}

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const currentValue = value?.toString() || ''
		const enteredDigits = extractDigits(currentValue)

		if (enteredDigits.length === 0 && currentValue) {
			onChange?.('' as any)
		}

		setIsFocused(false)
		onBlur?.(e)
	}

	const displayValue = value?.toString() || ''

	return (
		<Input
			{...inputProps}
			ref={inputRef}
			type="tel"
			label={label}
			value={displayValue}
			disabled={disabled}
			readOnly={readOnly}
			placeholder={isFocused ? '+7 (___) ___-__-__' : ''}
			onChange={handleChange}
			onKeyDown={handleKeyDown}
			onMouseDown={onMouseDown}
			onFocus={handleFocus}
			onBlur={handleBlur}
			className={clsx(className, displayValue.length < 2 && 'empty-phone-input')}
		/>
	)
}
